import React, { useEffect, useState, useMemo } from "react";
import api from "../lib/api";
import { Link } from "react-router-dom";
import { Info, MapPin, BookOpen, Pencil } from "lucide-react";

const DAY_LABELS = {
  monday: "Lundi", tuesday: "Mardi", wednesday: "Mercredi",
  thursday: "Jeudi", friday: "Vendredi", saturday: "Samedi", sunday: "Dimanche",
};
const DAY_ORDER = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];

const PlanningAdmin = () => {
  const [courses, setCourses] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get("/admin/courses"),
      api.get("/admin/teachers"),
    ]).then(([c, t]) => {
      setCourses(c.data || []);
      setTeachers(t.data || []);
    }).finally(() => setLoading(false));
  }, []);

  const days = useMemo(() => {
    const colorByTeacher = {};
    teachers.forEach((t) => { if (t?.name) colorByTeacher[t.name] = t.color || "bg-stone-200"; });
    const byDay = {};
    courses.forEach((c) => {
      const slots = Array.isArray(c.slots) ? c.slots : [];
      slots.forEach((s) => {
        const dayKey = s.day;
        if (!dayKey) return;
        if (!byDay[dayKey]) byDay[dayKey] = [];
        byDay[dayKey].push({
          courseId: c.id,
          courseName: c.name,
          category: c.category,
          teacher: s.teacher || "",
          room: s.room || "",
          startTime: s.startTime || "",
          endTime: s.endTime || "",
          color: c.color || colorByTeacher[s.teacher || ""] || "bg-stone-100",
        });
      });
    });
    return DAY_ORDER.filter((d) => byDay[d]).map((d) => ({
      dayKey: d,
      label: DAY_LABELS[d],
      slots: byDay[d].sort((a, b) => (a.startTime || "").localeCompare(b.startTime || "")),
    }));
  }, [courses, teachers]);

  return (
    <div data-testid="planning-admin">
      <div className="mb-4">
        <h1 className="font-serif-display text-2xl sm:text-3xl text-stone-800">Planning</h1>
        <p className="text-stone-500 mt-1 text-sm sm:text-base">Vue agrégée du planning hebdomadaire, générée automatiquement depuis les créneaux des cours.</p>
      </div>

      <div className="bg-blush-light/60 border border-blush-light rounded-2xl p-4 sm:p-5 mb-6 flex gap-3 items-start">
        <Info size={18} className="text-deep-rose shrink-0 mt-0.5"/>
        <div className="text-stone-700 text-sm">
          Pour ajouter, modifier ou supprimer un créneau, ouvrez la fiche du <Link to="/admin/courses" className="text-deep-rose underline font-semibold">cours concerné</Link> et éditez son champ « Créneaux dans la semaine ».
          Un cours peut avoir <strong>plusieurs créneaux</strong> dans la semaine.
        </div>
      </div>

      {loading ? (
        <div className="text-stone-500 p-8 text-center">Chargement...</div>
      ) : days.length === 0 ? (
        <div className="bg-white rounded-2xl p-8 text-center text-stone-500">
          Aucun créneau renseigné. Ouvrez un cours et ajoutez ses horaires.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {days.map((d) => (
            <div key={d.dayKey} className="bg-white rounded-2xl shadow-sm p-4">
              <h3 className="font-serif-display text-xl text-deep-rose mb-3">{d.label}</h3>
              <div className="space-y-2">
                {d.slots.map((s, i) => (
                  <Link
                    key={`${s.courseId}-${i}`}
                    to="/admin/courses"
                    className={`block rounded-lg p-2.5 hover:shadow-md transition-shadow ${s.color}`}
                  >
                    <div className="text-[10px] uppercase tracking-widest opacity-80">
                      {s.startTime} – {s.endTime}
                    </div>
                    <div className="font-serif-display text-sm leading-tight mt-0.5 flex items-center justify-between gap-2">
                      <span className="truncate">{s.courseName}</span>
                      <Pencil size={11} className="opacity-50 shrink-0"/>
                    </div>
                    <div className="text-xs opacity-80 mt-0.5 flex items-center gap-1.5">
                      <MapPin size={10}/> {s.room}
                      {s.teacher && <span>· {s.teacher}</span>}
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="mt-8 bg-white rounded-2xl shadow-sm p-4 sm:p-5">
        <h2 className="font-serif-display text-lg text-stone-800 mb-3 flex items-center gap-2">
          <BookOpen size={16} className="text-deep-rose"/> Cours sans créneau
        </h2>
        {(() => {
          const orphans = courses.filter((c) => !Array.isArray(c.slots) || c.slots.length === 0);
          if (orphans.length === 0) return <p className="text-stone-500 text-sm">Tous les cours ont au moins un créneau.</p>;
          return (
            <ul className="space-y-1">
              {orphans.map((c) => (
                <li key={c.id}>
                  <Link to="/admin/courses" className="text-deep-rose underline text-sm">{c.name}</Link>
                  <span className="text-stone-500 text-sm"> — ajouter un créneau</span>
                </li>
              ))}
            </ul>
          );
        })()}
      </div>
    </div>
  );
};

export default PlanningAdmin;
